/**
 * Access Control API Service
 *
 * Service functions for managing access control requests, whitelisted domains, and users.
 */

import { apiClient } from './client';
import type {
  AccessRequest,
  CreateAccessRequest,
  ApproveRequestBody,
  DenyRequestBody,
  PaginatedResponse,
  AccessControlSuccess,
  WhitelistedDomain,
  WhitelistedUser,
  CreateDomainRequest,
  CreateUserRequest,
  UpdateDomainRequest,
  ListDomainsQuery,
  ListUsersQuery,
  AccessCheckResult,
  AccessControlStats,
} from '../../types/access-control';

/**
 * Get access requests filtered by status
 */
export async function getAccessRequests(
  status?: 'pending' | 'approved' | 'denied'
): Promise<AccessRequest[]> {
  const params = new URLSearchParams();
  if (status) {
    params.append('status', status);
  }

  const endpoint = `/access-control/requests${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await apiClient.get<PaginatedResponse<AccessRequest>>(endpoint);
  return response.data;
}

/**
 * Approve an access request
 */
export async function approveAccessRequest(
  id: string,
  reviewNotes?: string,
  addToWhitelist?: boolean
): Promise<AccessControlSuccess> {
  const body: ApproveRequestBody = {};
  if (reviewNotes !== undefined) body.reviewNotes = reviewNotes;
  if (addToWhitelist !== undefined) body.addToWhitelist = addToWhitelist;

  return apiClient.post<AccessControlSuccess>(
    `/access-control/requests/${id}/approve`,
    body
  );
}

/**
 * Deny an access request
 */
export async function denyAccessRequest(
  id: string,
  reviewNotes?: string
): Promise<AccessControlSuccess> {
  const body: DenyRequestBody = {};
  if (reviewNotes !== undefined) body.reviewNotes = reviewNotes;

  return apiClient.post<AccessControlSuccess>(
    `/access-control/requests/${id}/deny`,
    body
  );
}

/**
 * Delete an access request
 * Permanently removes an access request from the system
 *
 * @param id - Access request ID
 * @returns Success response
 */
export async function deleteAccessRequest(
  id: string
): Promise<AccessControlSuccess> {
  return apiClient.delete<AccessControlSuccess>(
    `/access-control/requests/${id}`
  );
}

// ==================== Whitelisted Domains ====================

/**
 * Get all whitelisted domains
 */
export async function getWhitelistedDomains(
  query?: ListDomainsQuery
): Promise<PaginatedResponse<WhitelistedDomain>> {
  const params = new URLSearchParams();
  if (query?.autoApprove !== undefined) params.append('autoApprove', String(query.autoApprove));
  if (query?.limit !== undefined) params.append('limit', String(query.limit));
  if (query?.offset !== undefined) params.append('offset', String(query.offset));

  const queryString = params.toString();
  const url = `/access-control/domains${queryString ? `?${queryString}` : ''}`;

  return apiClient.get<PaginatedResponse<WhitelistedDomain>>(url);
}

/**
 * Get a single whitelisted domain by ID
 */
export async function getWhitelistedDomain(id: string): Promise<WhitelistedDomain> {
  return apiClient.get<WhitelistedDomain>(`/access-control/domains/${id}`);
}

/**
 * Add a new whitelisted domain
 */
export async function addWhitelistedDomain(
  data: CreateDomainRequest
): Promise<WhitelistedDomain> {
  return apiClient.post<WhitelistedDomain>('/access-control/domains', data);
}

/**
 * Update a whitelisted domain
 */
export async function updateWhitelistedDomain(
  id: string,
  data: UpdateDomainRequest
): Promise<WhitelistedDomain> {
  return apiClient.patch<WhitelistedDomain>(`/access-control/domains/${id}`, data);
}

/**
 * Delete a whitelisted domain
 */
export async function deleteWhitelistedDomain(id: string): Promise<void> {
  return apiClient.delete(`/access-control/domains/${id}`);
}

// ==================== Whitelisted Users ====================

/**
 * Get all whitelisted users
 */
export async function getWhitelistedUsers(
  query?: ListUsersQuery
): Promise<PaginatedResponse<WhitelistedUser>> {
  const params = new URLSearchParams();
  if (query?.limit !== undefined) params.append('limit', String(query.limit));
  if (query?.offset !== undefined) params.append('offset', String(query.offset));
  if (query?.search) params.append('search', query.search);

  const queryString = params.toString();
  const url = `/access-control/users${queryString ? `?${queryString}` : ''}`;

  return apiClient.get<PaginatedResponse<WhitelistedUser>>(url);
}

/**
 * Get a single whitelisted user by ID
 */
export async function getWhitelistedUser(id: string): Promise<WhitelistedUser> {
  return apiClient.get<WhitelistedUser>(`/access-control/users/${id}`);
}

/**
 * Add a new whitelisted user
 */
export async function addWhitelistedUser(
  data: CreateUserRequest
): Promise<WhitelistedUser> {
  return apiClient.post<WhitelistedUser>('/access-control/users', data);
}

/**
 * Delete a whitelisted user
 */
export async function deleteWhitelistedUser(id: string): Promise<void> {
  return apiClient.delete(`/access-control/users/${id}`);
}

// ==================== Public Access Request API ====================

/**
 * Submit an access request (public endpoint, no auth required)
 * Used when a user tries to sign in but is not whitelisted
 */
export async function submitAccessRequest(
  data: CreateAccessRequest
): Promise<AccessRequest> {
  // This endpoint is public and doesn't require authentication
  // We'll use a direct fetch instead of apiClient to avoid auth headers
  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

  const response = await fetch(`${API_BASE}/access-control/requests`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Request failed: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Check access status for an email (public endpoint)
 * Checks if a user has access or an existing request
 *
 * @param email - Email address to check
 * @returns Access check result with status
 */
export async function checkAccessRequestStatus(
  email: string
): Promise<AccessCheckResult> {
  // This endpoint is public and doesn't require authentication
  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

  const response = await fetch(
    `${API_BASE}/access-control/requests/check/${encodeURIComponent(email)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Request failed: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get access control statistics for admin dashboard
 *
 * @returns Statistics summary
 */
export async function getAccessControlStats(): Promise<AccessControlStats> {
  return apiClient.get<AccessControlStats>('/access-control/stats');
}

/**
 * Create a new whitelisted domain (alias for consistency)
 */
export async function createWhitelistedDomain(
  data: CreateDomainRequest
): Promise<WhitelistedDomain> {
  return addWhitelistedDomain(data);
}

/**
 * Create a new whitelisted user (alias for consistency)
 */
export async function createWhitelistedUser(
  data: CreateUserRequest
): Promise<WhitelistedUser> {
  return addWhitelistedUser(data);
}
